<template>
  <VueFinalModal
    :click-to-close="true"
    :esc-to-close="true"
    :focus-trap="modalFocusTrap"
    class="final-modal"
    overlay-transition="vfm-fade"
    :class="{
      'theme--default': theme === 'light',
      'theme--dark': theme === 'dark',
    }"
  >
    <template
      #default="{
        // @ts-ignore - vue-final-modal exposes `close` through slot
        close,
      }"
    >
      <div
        class="content"
        aria-modal="true"
        role="dialog"
        :aria-labelledby="`catalogAgentTitle-${entry.key}`"
        tabindex="-1"
      >
        <div class="header">
          <h4 :id="`catalogAgentTitle-${entry.key}`" class="title">
            {{ entry.name }}
          </h4>
          <BaseButton kind="auxiliary" class="close-icon" @click="close">
            <template #icon>
              <CloseIcon />
            </template>
          </BaseButton>
        </div>

        <div class="body">
          <div class="agent-profile">
            <div class="agent-avatar">
              <img
                v-if="entry.avatar"
                :src="entry.avatar"
                :alt="entry.name"
                class="avatar-img"
              />
              <div v-else class="avatar-placeholder">
                {{ entry.name.charAt(0) }}
              </div>
            </div>
            <div class="agent-meta">
              <div class="agent-name-row">
                <span class="agent-name">{{ entry.name }}</span>
                <span v-if="entry.tag" class="agent-tag">{{ entry.tag }}</span>
              </div>
              <span class="agent-role">{{ entry.description }}</span>
            </div>
          </div>

          <div v-if="promptText" class="prompt-section">
            <p class="prompt-label">Instructions</p>
            <pre class="prompt-content">{{ promptText }}</pre>
          </div>

          <div
            v-if="entry.examples && entry.examples.length > 0"
            class="examples-section"
          >
            <p class="examples-label">Example prompts</p>
            <ul class="examples-list">
              <li
                v-for="example in entry.examples"
                :key="example"
                class="example-item"
              >
                {{ example }}
              </li>
            </ul>
          </div>
        </div>

        <div class="footer">
          <template v-if="isActive">
            <BaseButton
              kind="main"
              type="secondary"
              text="Open"
              @click="handleOpen(close)"
            />
            <BaseButton
              kind="main"
              type="secondary"
              tint="red"
              text="Disable"
              :state="actionState"
              @click="handleDisable(close)"
            />
          </template>
          <BaseButton
            v-else
            kind="main"
            type="primary"
            text="Enable"
            :state="actionState"
            @click="handleEnable(close)"
          />
        </div>
      </div>
    </template>
  </VueFinalModal>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue";
import { VueFinalModal } from "vue-final-modal";

import type { CatalogAgent } from "@/components/ai/useAgents";
import BaseButton from "@/components/base/BaseButton.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import * as inject from "@/components/inject";
import { modalFocusTrap } from "@/components/modals/focus-trap";

import { getAgentTemplate } from "./prompt";

export default defineComponent({
  name: "CatalogAgentModal",
  components: {
    BaseButton,
    CloseIcon,
    VueFinalModal,
  },
  props: {
    entry: {
      type: Object as PropType<CatalogAgent>,
      required: true,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    onEnable: {
      type: Function as PropType<() => Promise<void>>,
      required: true,
    },
    onOpen: {
      type: Function as PropType<() => Promise<void>>,
      required: true,
    },
    onDisable: {
      type: Function as PropType<() => Promise<void>>,
      required: true,
    },
  },
  setup(props) {
    const theme = inject.theme();
    const isLoading = ref(false);

    const promptText = computed(() => {
      const template = getAgentTemplate(props.entry.key);
      return template || props.entry.description;
    });

    const actionState = computed(() =>
      isLoading.value ? "loading" : "default"
    );

    const handleEnable = (close: () => void) => {
      isLoading.value = true;
      props
        .onEnable()
        .then(() => {
          close();
        })
        .finally(() => {
          isLoading.value = false;
        });
    };

    const handleOpen = (close: () => void) => {
      props.onOpen().then(() => {
        close();
      });
    };

    const handleDisable = (close: () => void) => {
      isLoading.value = true;
      props
        .onDisable()
        .then(() => {
          close();
        })
        .finally(() => {
          isLoading.value = false;
        });
    };

    return {
      modalFocusTrap,
      theme,
      promptText,
      actionState,
      handleEnable,
      handleOpen,
      handleDisable,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/_variables.scss" as *;
@use "@/styles/_themes.scss" as *;

.content {
  display: flex;
  flex-direction: column;
  border-radius: $border-radius-l;
  max-width: 620px;
  min-width: 620px;
  max-height: 80vh;
  box-shadow: 0px 1px 32px 0px rgba(0, 0, 0, 0.12);

  @include themed() {
    background-color: t($bg);
    color: t($primary);
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;

  @include themed() {
    border-bottom: 1px solid t($grey-15);
  }
}

.title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 16px 24px;
  overflow-y: auto;
  min-height: 0;
}

.agent-profile {
  display: flex;
  align-items: center;
  gap: 14px;
}

.agent-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    font-weight: 500;

    @include themed() {
      background-color: t($tertiary-50);
      color: t($bg);
    }
  }
}

.agent-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.agent-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.agent-name {
  @extend %font-body-medium;
  @include themed() {
    color: t($primary);
  }
}

.agent-tag {
  @extend %font-small-medium;
  padding: 2px 8px;
  border-radius: $border-radius-m;

  @include themed() {
    color: t($tertiary-50);
    background-color: t($tertiary-10);
  }
}

.agent-role {
  @extend %font-body-regular;
  @include themed() {
    color: t($grey-60);
  }
}

.examples-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.examples-label {
  margin: 0;
  @extend %font-caption-medium;
  @include themed() {
    color: t($grey-60);
  }
}

.examples-list {
  margin: 0;
  padding: 0;
  list-style: disc inside;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.example-item {
  @extend %font-body-regular;
  @include themed() {
    color: t($grey-70);
  }
}

.prompt-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.prompt-label {
  margin: 0;
  @extend %font-caption-medium;
  @include themed() {
    color: t($grey-60);
  }
}

.prompt-content {
  margin: 0;
  padding: 12px 14px;
  border-radius: $border-radius-m;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  min-width: 0;

  @include themed() {
    background-color: t($bg-alt);
    border: 1px solid t($grey-10);
    color: t($grey-70);
  }
}

.footer {
  padding: 12px 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  @include themed() {
    border-top: 1px solid t($grey-10);
  }
}
</style>
